/**
 * Publieke blogindex — data uit D1 via GET /api/blog/posts.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';

function fmtDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(iso)
    );
  } catch {
    return iso;
  }
}

export function Blog() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: () => api.getPublicBlogPosts(),
  });

  const posts = data?.posts ?? [];
  const errMsg = error instanceof Error ? error.message : '';

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/blog"
        title="Blog — bouwkosten, renovatie en CalqTool"
        description="Artikelen over bouwkosten, renovatie, transformatie, verduurzaming en regelgeving in Nederland — met praktische tips en hoe CalqTool™ je calculatie en export ondersteunt."
        keywords="CalqTool blog, bouwkosten, renovatie, calculatie, modelbegroting, Nederlandse bouw, transformatie, verduurzaming, AFM woning, fundering"
      />
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Blog' }]} />

      <header className="mb-10 border-b border-border pb-8">
        <h1 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">Blog</h1>
        <p className="mt-4 max-w-2xl text-lg text-text-muted">
          Inzichten voor calculatoren, werkvoorbereiders en bouwteams: kosten, regelgeving en software — met de nadruk op
          transparante begrotingen. Voor{' '}
          <Link to="/prijzen" className="font-medium text-primary hover:underline">
            pakketten en prijzen
          </Link>{' '}
          of een{' '}
          <Link to="/demo" className="font-medium text-primary hover:underline">
            product-demo
          </Link>
          .
        </p>
      </header>

      {isLoading ? (
        <p className="text-text-muted">Laden…</p>
      ) : error ? (
        <p className="text-red-600 dark:text-red-400" role="alert">
          {errMsg || 'Kon blog niet laden.'}
        </p>
      ) : posts.length === 0 ? (
        <p className="text-text-muted">Er zijn nog geen artikelen gepubliceerd.</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          {posts.map((p, idx) => (
            <AnimatedSection key={p.slug} delay={Math.min(idx * 0.04, 0.24)}>
              <article className="flex h-full flex-col rounded-2xl border border-border bg-surface-card p-5 shadow-sm transition hover:border-primary/30 hover:shadow-md">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-text-muted">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" aria-hidden />
                    <time dateTime={p.published_at}>{fmtDate(p.published_at)}</time>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    {p.reading_minutes} min lezen
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Tag className="h-3.5 w-3.5" aria-hidden />
                    {p.category}
                  </span>
                </div>
                <h2 className="text-lg font-semibold leading-snug text-text">
                  <Link to={`/blog/${encodeURIComponent(p.slug)}`} className="text-text hover:text-primary">
                    {p.title}
                  </Link>
                </h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-text-muted">{p.excerpt}</p>
                <Link
                  to={`/blog/${encodeURIComponent(p.slug)}`}
                  className="mt-4 inline-flex text-sm font-medium text-primary hover:underline"
                >
                  Lees verder →
                </Link>
              </article>
            </AnimatedSection>
          ))}
        </div>
      )}
    </div>
  );
}
